import { Group, Modal, Stack, useMantineTheme } from '@mantine/core';
import { useSuspenseRetrievePrompt } from '@repo/api-codegen';
import { UserAvatar } from '@repo/common/components';
import { Button, Icon, Text } from '@repo/foundations';
import { memo, Suspense, useMemo } from 'react';
import { Link } from 'react-router';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import { AiChatPreviewModalSkeleton } from './components/AiChatPreviewModalSkeleton';
import { MessageGroup } from './components/MessageGroup';

interface AiChatPreviewModalControllerProps {
	promptId: string;
}

function ErrorFallback() {
	return (
		<Modal.Content>
			<Modal.Header>
				<Modal.Title>
					<Text size="xs" weight="semibold">
						Chat Preview
					</Text>
				</Modal.Title>
				<Modal.CloseButton />
			</Modal.Header>
			<Modal.Body>
				<Stack align="center" spacing="xs" py="xl">
					<Icon name="lock" size="lg" color="icon/primary/default" />
					<Text color="text/primary/default" fw="semibold">
						No access to this resource
					</Text>
					<Text size="sm" color="text/secondary/default">
						You do not have permissions to access this resource
					</Text>
				</Stack>
			</Modal.Body>
		</Modal.Content>
	);
}

const AiChatPreviewModalControllerInternal = memo(
	({ promptId }: AiChatPreviewModalControllerProps) => {
		const theme = useMantineTheme();
		const { data: prompt } = useSuspenseRetrievePrompt(
			{
				pathParams: {
					promptId,
				},
			},
			// Don't retry if the prompt is not found so we show the error fallback quicker
			{
				retry: false,
			}
		);

		const prompts = useMemo(
			() => [prompt, ...(prompt.children ?? [])],
			[prompt]
		);

		return (
			<Modal.Content>
				<Modal.Header>
					<Group position="apart" spacing="sm" w="100%">
						<Group spacing="sm">
							{prompt.user && <UserAvatar user={prompt.user} size="md" />}
							<Text size="xs" fw={500}>
								{prompt.user?.display_name}&apos;s chat with Secoda AI
							</Text>
						</Group>
						<Button
							component={Link}
							to={`/ai/${prompt.id}`}
							target="_blank"
							variant="tertiary"
							rightIconName="chevronRight"
							mr="3xs"
						>
							Open in Secoda AI
						</Button>
					</Group>
					<Modal.CloseButton />
				</Modal.Header>
				<Modal.Body style={{ padding: `${theme.spacing.xl}px !important` }}>
					<Stack spacing="5xl">
						{prompts.map((item) => (
							<MessageGroup key={item.id} prompt={item} />
						))}
					</Stack>
				</Modal.Body>
			</Modal.Content>
		);
	}
);

export const AiChatPreviewModalController = memo(
	({
		onClose,
		...props
	}: AiChatPreviewModalControllerProps & { onClose: () => void }) => (
		<Modal.Root opened onClose={onClose} size="xl" centered>
			<Modal.Overlay />
			<ErrorBoundary fallback={ErrorFallback}>
				<Suspense fallback={<AiChatPreviewModalSkeleton />}>
					<AiChatPreviewModalControllerInternal {...props} />
				</Suspense>
			</ErrorBoundary>
		</Modal.Root>
	)
);
